import type { PluralForm, TranslationFunction } from "./types";

/** Exact evidence captured after key resolution and interpolation for one runtime translation request. */
export type TranslationLookup = {
  /** Key requested by the caller before plural resolution. */
  key: string;
  /** Catalog key that supplied the rendered template, including any plural suffix. */
  resolvedKey: string;
  /** Active catalog locale when the lookup occurred. */
  language: string;
  /** Catalog value before variable substitution. */
  template: string;
  /** Final value returned to the caller. */
  value: string;
};

/** Creates the translation function and emits lookup evidence only after a value resolves successfully. */
export function createT<T extends Record<string, string>>({
  translations,
  language,
  onMissingKey,
  onMissingVariable,
  onLookup,
}: {
  translations: T;
  language: string;
  onMissingKey?: (key: string, language: string) => void;
  onMissingVariable?: (key: string, value: string, language: string) => void;
  /** Receives the requested and resolved keys after plural selection and interpolation. */
  onLookup?: (lookup: TranslationLookup) => void;
}): TranslationFunction<T> {
  return (key: keyof T, variables?: { count?: number; [key: string]: any }): string => {
    let result: string | undefined;
    let resolvedKey = key as string;

    // We determine the plural form based on the count variable.
    // If it's not available we fall back to the non-plural form, and log it.
    if (typeof variables?.count === "number") {
      const pluralForm = getPluralForm(language, variables.count);
      const pluralKey = `${key as string}_${pluralForm}` as keyof typeof translations;
      result = translations[pluralKey];
      if (result != null) {
        resolvedKey = pluralKey as string;
      }

      const otherKey = `${key as string}_other` as keyof typeof translations;
      if (result == null && translations[otherKey] != null) {
        result = translations[otherKey];
        resolvedKey = otherKey as string;
      }
      if (result == null && translations[key] != null) {
        result = translations[key];
        resolvedKey = key as string;
      }
    } else {
      result = translations[key];
    }

    if (result == null) {
      onMissingKey?.(key as string, language);

      return key as string;
    }

    const template = result;

    if (variables) {
      result = substitute(result, variables);
    }

    if (containsVariables(result)) {
      onMissingVariable?.(key as string, result, language);
    }

    onLookup?.({ key: key as string, resolvedKey, language, template, value: result });

    return result;
  };
}

const pluralRules: Record<string, Intl.PluralRules> = {};
function getPluralForm(lang: string, count: number): PluralForm {
  let pluralRule = pluralRules[lang];
  if (!pluralRule) {
    pluralRules[lang] = new Intl.PluralRules(lang);
    pluralRule = pluralRules[lang];
  }

  return pluralRule.select(count);
}

const substituteRegex = /{{\s*(\w+)\s*}}/g;
function substitute(translation: string, variables: { [key: string]: string }): string {
  return translation.replaceAll(substituteRegex, (group, variable) => {
    const value = variables[variable as string];
    if (value == null) {
      return group;
    }

    if (typeof value === "string") {
      return value;
    }

    return String(value);
  });
}

function containsVariables(translation: string): boolean {
  // translation.includes pre-check makes this faster in the common case.
  substituteRegex.lastIndex = 0;

  return translation.includes("{{") && substituteRegex.test(translation);
}
